import * as Sentry from '@sentry/react';
import config from '../config';

// minimal abstraction to avoid direct dependency on Sentry, similar to what we do in other apps

interface Context {
  user?: Sentry.User;
  level?: Sentry.Severity;
  extra?: Record<string, unknown>;
  contexts?: Record<string, Record<string, unknown>>;
  tags?: {
    [key: string]: number | string | boolean | bigint | symbol | null | undefined;
  };
  fingerprint?: string[];
}

export function addBreadcrumb({
  category,
  type,
  level = Sentry.Severity.Info,
  message,
  data,
}: {
  category: string;
  message: string;
  level?: Sentry.Severity;
  type?: string;
  data?: { [key: string]: unknown };
}) {
  Sentry.addBreadcrumb({
    category,
    type,
    message,
    level,
    data,
  });
}

export function logMessage(message: string, level: Sentry.Severity) {
  // eslint-disable-next-line no-console
  console.info(message);

  if (config.appEnv !== 'development') {
    Sentry.captureMessage(message, level);
  }
}

export function logException(error: Error, context?: Context) {
  // eslint-disable-next-line no-console
  console.error(error);

  if (config.appEnv !== 'development') {
    Sentry.captureException(error, context);
  }
}

export const logger = {
  breadcrumb: addBreadcrumb,
  debug: (message: string) => logMessage(message, Sentry.Severity.Debug),
  warn: (error: Error, context?: Omit<Context, 'level'>) =>
    logException(error, {
      ...context,
      level: Sentry.Severity.Warning,
    }),
  error: (error: Error, context?: Omit<Context, 'level'>) =>
    logException(error, {
      ...context,
      level: Sentry.Severity.Error,
    }),
};
